/**
 * @file formRuntimeEngine.ts
 * @description Centralized runtime engine for the dynamic form system.
 *
 * Combines:
 *  1. Full validation (all 20+ rule types + cross-field)
 *  2. Computed field evaluation (safe formula engine)
 *  3. Dependency graph construction and targeted recomputation
 *
 * This is the "brain" of the runtime form — consumed by DynamicFormPage.
 */

import type {
  FormFieldDefinition,
} from '../../types/formBuilder';
import type { FieldValue } from './dynamicFormFieldUtils';

// ═══════════════════════════════════════════════════════════════════════════
//  1. RUNTIME VALIDATION ENGINE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Validate a single field's value against its `validationRules` array.
 * Returns an error message string, or null if valid.
 *
 * This evaluates ALL validation rule types — not just mandatory.
 */
export function validateFieldRules(
  field: FormFieldDefinition,
  value: FieldValue,
  isMandatory: boolean,
): string | null {
  const val = value;

  // ── Required check (respects conditional REQUIRE/UNREQUIRE overrides) ──
  if (isMandatory) {
    if (val === undefined || val === null || val === '') {
      return `${field.label} is required`;
    }
    if (field.fieldType === 'MULTISELECT' && Array.isArray(val) && val.length === 0) {
      return `${field.label} is required`;
    }
    if (field.fieldType === 'CHECKBOX' && val !== true) {
      return `${field.label} must be checked`;
    }
    if (field.fieldType === 'TERMS_AND_CONDITIONS' && val !== true) {
      return 'You must accept the terms and conditions';
    }
    if (field.fieldType === 'REPEATABLE_GROUP' && (!Array.isArray(val) || val.length === 0)) {
      return `${field.label} requires at least one entry`;
    }
  }

  // Skip further rules if value is empty (non-required field)
  if (val === undefined || val === null || val === '') return null;

  const strVal = String(val);
  const numVal = Number(val);
  const rules = field.validationRules ?? [];

  for (const rule of rules) {
    const msg = rule.errorMessage || undefined;

    switch (rule.type) {
      case 'MIN_LENGTH':
        if (rule.value && strVal.length < Number(rule.value)) {
          return msg ?? `${field.label} must be at least ${rule.value} characters`;
        }
        break;

      case 'MAX_LENGTH':
        if (rule.value && strVal.length > Number(rule.value)) {
          return msg ?? `${field.label} must be at most ${rule.value} characters`;
        }
        break;

      case 'MIN_VALUE':
        if (rule.value && !isNaN(numVal) && numVal < Number(rule.value)) {
          return msg ?? `${field.label} must be at least ${rule.value}`;
        }
        break;

      case 'MAX_VALUE':
        if (rule.value && !isNaN(numVal) && numVal > Number(rule.value)) {
          return msg ?? `${field.label} must be at most ${rule.value}`;
        }
        break;

      case 'PATTERN':
        if (rule.value) {
          try {
            const regex = new RegExp(rule.value);
            if (!regex.test(strVal)) {
              return msg ?? `${field.label} format is invalid`;
            }
          } catch {
            // Invalid regex — skip
          }
        }
        break;

      case 'EMAIL':
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(strVal)) {
          return msg ?? `${field.label} must be a valid email address`;
        }
        break;

      case 'PHONE':
        if (!/^[+]?[\d\s()-]{7,20}$/.test(strVal)) {
          return msg ?? `${field.label} must be a valid phone number`;
        }
        break;

      case 'FILE_SIZE': {
        // value is max size in MB; field value might be an uploaded file object
        if (typeof val === 'object' && val !== null && 'size' in val) {
          const maxBytes = Number(rule.value) * 1024 * 1024;
          if ((val as { size: number }).size > maxBytes) {
            return msg ?? `${field.label} exceeds maximum file size of ${rule.value} MB`;
          }
        }
        break;
      }

      case 'FILE_TYPE': {
        if (typeof val === 'object' && val !== null && 'name' in val && rule.value) {
          const ext = (val as { name: string }).name.split('.').pop()?.toLowerCase() ?? '';
          const allowed = rule.value.split(',').map((s: string) => s.trim().toLowerCase());
          if (!allowed.includes(ext)) {
            return msg ?? `${field.label} must be one of: ${rule.value}`;
          }
        }
        break;
      }

      case 'MIN_DATE': {
        if (rule.value && strVal < rule.value) {
          return msg ?? `${field.label} must be on or after ${rule.value}`;
        }
        break;
      }

      case 'MAX_DATE': {
        if (rule.value && strVal > rule.value) {
          return msg ?? `${field.label} must be on or before ${rule.value}`;
        }
        break;
      }

      case 'PAST_DATES_ONLY': {
        const today = new Date().toISOString().split('T')[0];
        if (strVal > today) {
          return msg ?? `${field.label} must be a past date`;
        }
        break;
      }

      case 'FUTURE_DATES_ONLY': {
        const today = new Date().toISOString().split('T')[0];
        if (strVal < today) {
          return msg ?? `${field.label} must be a future date`;
        }
        break;
      }

      case 'MIN_AGE': {
        if (rule.value) {
          const dob = new Date(strVal);
          const ageMs = Date.now() - dob.getTime();
          const age = Math.floor(ageMs / (365.25 * 24 * 60 * 60 * 1000));
          if (age < Number(rule.value)) {
            return msg ?? `Minimum age is ${rule.value} years`;
          }
        }
        break;
      }

      case 'MAX_AGE': {
        if (rule.value) {
          const dob = new Date(strVal);
          const ageMs = Date.now() - dob.getTime();
          const age = Math.floor(ageMs / (365.25 * 24 * 60 * 60 * 1000));
          if (age > Number(rule.value)) {
            return msg ?? `Maximum age is ${rule.value} years`;
          }
        }
        break;
      }

      // DATE_RANGE is a combination min+max — handled by MIN_DATE / MAX_DATE above

      // REQUIRED, AUTO_POPULATE, USERNAME_SOURCE — no additional validation needed
      default:
        break;
    }
  }

  return null;
}

/**
 * Validate ALL fields in one pass, respecting visibility and required overrides.
 * Returns a map of fieldInternalName → error message.
 */
export function validateAllFields(
  fields: FormFieldDefinition[],
  formData: Record<string, FieldValue>,
  visibility: Record<string, boolean>,
  requiredOverrides: Record<string, boolean | undefined>,
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const field of fields) {
    // Skip hidden fields — they should not be validated
    if (visibility[field.fieldId] === false) continue;

    // Skip display-only types
    if (['PARAGRAPH', 'HEADER', 'DISPLAY', 'FIELD_GROUP'].includes(field.fieldType)) continue;

    // Skip non-editable, system/server-populated types — the user has no
    // control to satisfy "required" on a field they cannot type into, and
    // the server fills these in (e.g. patientCode) independently of what the
    // client submits.
    if (['AUTO_SEQUENCE', 'AUTO_GENERATED', 'SYSTEM_FIELD', 'LOOKUP_READ_ONLY', 'READ_ONLY'].includes(field.fieldType)) continue;

    // Determine effective required state
    const override = requiredOverrides[field.fieldId];
    const isMandatory = override !== undefined ? override : !!field.mandatory;

    const error = validateFieldRules(field, formData[field.internalName], isMandatory);
    if (error) {
      errors[field.internalName] = error;
    }
  }

  return errors;
}

// ═══════════════════════════════════════════════════════════════════════════
//  2. COMPUTED FIELD ENGINE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Safe formula evaluator.
 *
 * Supports references to other field values via their internalName, e.g.:
 *   "approvedAmount - utilizedAmount"
 *   "quantity * unitPrice"
 *   "dob ? Math.floor((Date.now() - new Date(dob).getTime()) / 31557600000) : ''"
 *
 * Security: Uses Function constructor with a frozen data proxy — no access to
 * global scope, window, document, fetch, etc. All property access goes through
 * the proxy which only exposes form data keys.
 */
export function evaluateFormula(
  formula: string,
  formData: Record<string, FieldValue>,
): FieldValue {
  if (!formula?.trim()) return null;

  try {
    // Build a safe sandbox — only form data keys are accessible
    const keys = Object.keys(formData);
    const values = keys.map((k) => formData[k]);

    // Construct a function whose parameters are the field names
    // eslint-disable-next-line no-new-func
    const fn = new Function(...keys, `"use strict"; return (${formula});`);
    const result = fn(...values);

    // Guard against infinite / NaN leaking into form data
    if (typeof result === 'number' && !isFinite(result)) return null;
    return result as FieldValue;
  } catch {
    return null;
  }
}

/**
 * Evaluate all computed fields and merge their values into formData.
 * Returns a new formData object (does not mutate).
 */
export function evaluateComputedFields(
  fields: FormFieldDefinition[],
  formData: Record<string, FieldValue>,
): Record<string, FieldValue> {
  const updated = { ...formData };

  for (const field of fields) {
    if (field.fieldType !== 'COMPUTED') continue;
    const formula = field.properties?.formula as string | undefined;
    if (!formula) continue;

    const result = evaluateFormula(formula, updated);
    if (result !== null) {
      updated[field.internalName] = result;
    }
  }

  return updated;
}

// ═══════════════════════════════════════════════════════════════════════════
//  3. DEPENDENCY GRAPH
// ═══════════════════════════════════════════════════════════════════════════

export interface DependencyGraph {
  /** Maps fieldInternalName → set of fieldInternalNames that depend on it */
  dependents: Map<string, Set<string>>;
  /** Maps fieldInternalName → set of fieldInternalNames it depends on */
  dependencies: Map<string, Set<string>>;
}

/**
 * Build a dependency graph from form fields.
 * Sources of dependency:
 *  - conditionalLogic[].dependsOnField
 *  - dataSource.dependsOnFieldId (API-driven cascading dropdowns)
 *  - computed field formula references
 */
export function buildDependencyGraph(fields: FormFieldDefinition[]): DependencyGraph {
  const dependents = new Map<string, Set<string>>();
  const dependencies = new Map<string, Set<string>>();

  const addEdge = (from: string, to: string) => {
    if (!dependents.has(from)) dependents.set(from, new Set());
    dependents.get(from)!.add(to);
    if (!dependencies.has(to)) dependencies.set(to, new Set());
    dependencies.get(to)!.add(from);
  };

  // Build a map of fieldId → internalName for cross-referencing
  const idToName = new Map<string, string>();
  for (const f of fields) {
    idToName.set(f.fieldId, f.internalName);
  }

  for (const field of fields) {
    // Conditional logic deps
    if (field.conditionalLogic?.length) {
      for (const rule of field.conditionalLogic) {
        if (rule.dependsOnField) {
          // dependsOnField is internalName
          addEdge(rule.dependsOnField, field.internalName);
        }
      }
    }

    // API data source cascading deps
    if (field.dataSource?.dependsOnFieldId) {
      const parentName = idToName.get(field.dataSource.dependsOnFieldId) ?? field.dataSource.dependsOnFieldId;
      addEdge(parentName, field.internalName);
    }

    // Computed field formula deps
    if (field.fieldType === 'COMPUTED' && field.properties?.formula) {
      const formula = field.properties.formula as string;
      // Extract field references from formula by matching known internalNames
      for (const f2 of fields) {
        if (f2.internalName !== field.internalName && formula.includes(f2.internalName)) {
          addEdge(f2.internalName, field.internalName);
        }
      }
    }
  }

  return { dependents, dependencies };
}

/**
 * Given a changed field, return the set of all fields that need recomputation.
 * Uses BFS to traverse the dependency graph transitively.
 */
export function getAffectedFields(
  changedField: string,
  graph: DependencyGraph,
): Set<string> {
  const affected = new Set<string>();
  const queue = [changedField];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const deps = graph.dependents.get(current);
    if (deps) {
      for (const dep of deps) {
        if (!affected.has(dep)) {
          affected.add(dep);
          queue.push(dep);
        }
      }
    }
  }

  return affected;
}
